<template>
  <div id="cvcResult">
    <div
      v-if="cvcResult.type && cvcResult.type.length"
      class="leftLabel defaultCursor"
      :class="['desktop', 'laptop'].includes($mq) ? 'deskMode' : 'mobileMode'"
    >
      <div
        v-if="['desktop', 'laptop'].includes($mq)"
        class="overall"
      >
        {{ $t('oa.cvc.overallSatisfaction') }}: {{ emptyFlag ? '-' : getOverall }}%
        <el-tooltip
          effect="dark"
          placement="top"
          :content="$t('oa.cvc.overallTooltip')"
        >
          <i class="el-icon-info"></i>
        </el-tooltip>
      </div>
      <div
        v-for="(item, index) in graphLabel"
        :key="`deskLabel${index}`"
      >
        {{ item }}
      </div>
    </div>
    <div class="rightGraph">
      <div
        ref="chartdiv"
        class="hello"
        :class="emptyFlag ? 'dummyChart' : ''"
      ></div>
      <div
        class="bottomLabel defaultCursor"
        :class="['desktop', 'laptop'].includes($mq) ? 'deskMode' : 'mobileMode'"
      >
        <div
          v-for="(item, index) in bottomLabel"
          :key="`deskLabel${index}`"
        >
          {{ item }}
        </div>
      </div>
      <div
        v-if="emptyFlag"
        class="noData"
      >
        <div class="placeholder">
          {{ $t('oa.cvc.noData') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

am4core.useTheme(am4themesAnimated);

export default {
  props: {
    cvcResult: {
      type: Object,
      default: null,
    },

    emptyFlag: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      chart: null,
    };
  },

  computed: {
    resultCheck() {
      return this.cvcResult.type && this.cvcResult.type.length;
    },

    graphLabel() {
      let labels = [];
      if (['desktop', 'laptop'].includes(this.$mq)) {
        [...Array(5).keys()].forEach((el) => {
          labels.push(`${el + 1}. ${this.$t(`oa.cvc.label_${el + 1}`)}`);
        });
      } else {
        labels = ['Q1', 'Q2', 'Q3', 'Q4', 'Q5'];
      }
      return labels;
    },

    bottomLabel() {
      let labels = [];
      if (this.$mq === 'mobile') {
        labels = [
          '-2',
          '-1',
          '0',
          '1',
          '2',
        ];
      } else {
        [...Array(5).keys()].forEach((el) => {
          labels.push(`${this.$t(`oa.cvc.score_${el + 1}`)}`);
        });
      }
      return labels;
    },

    getOverall() {
      let over = 'n';
      if (this.cvcResult && this.cvcResult.list) {
        const question = [];
        this.cvcResult.list.forEach((cvc) => {
          if (cvc.question === 'Q5') {
            question.push(cvc);
          }
        });

        if (question && question.length) {
          let sum = 0;
          question.forEach((item, index) => {
            if (item[`cvc${index + 1}`]) {
              sum += item[`cvc${index + 1}`];
            }
          });
          over = Math.round((sum / 2 / question.length) * 100);
        }
      }

      return over;
    },

    fontColor() {
      return getComputedStyle(document.getElementById('app')).getPropertyValue('--themeFontColor');
    },
  },

  watch: {
    cvcResult(newCvc) {
      if (newCvc && newCvc.list) {
        this.disposeGraph();
        this.drawGraph();
      }
    },
  },

  mounted() {
    if (this.resultCheck) {
      this.drawGraph();
    }
  },

  beforeDestroy() {
    this.disposeGraph();
  },

  methods: {
    disposeGraph() {
      if (this.chart) {
        this.chart.dispose();
      }
    },

    drawGraph() {
      am4core.addLicense(process.env.VUE_APP_AMCHARTS_LICENSE);
      this.chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);
      this.chart.data = this.cvcResult.list;

      // Hide Y axis number
      // const format = this.chart.numberFormatter;
      // format.numberFormat = '';

      this.createX();
      this.createY();
      // Make double graph
      this.cvcResult.type.forEach((item, index) => {
        this.createSeries(item, index + 1);
      });
      this.createLegend();
    },

    createY() {
      // Create Y axes
      const categoryAxis = this.chart.yAxes.push(new am4charts.CategoryAxis());
      // // Label
      categoryAxis.dataFields.category = 'question';
      categoryAxis.renderer.inversed = true;
      categoryAxis.renderer.labels.template.fill = am4core.color('rgba(255, 255, 255, 0)');
      // // Grid
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = 30;
      categoryAxis.renderer.grid.template.stroke = am4core.color(this.fontColor);
      categoryAxis.renderer.grid.template.strokeOpacity = 0.5;

      // Cell padding
      categoryAxis.renderer.cellStartLocation = 0.15;
      categoryAxis.renderer.cellEndLocation = 0.85;
    },

    createX() {
      // Create X axes
      const valueAxis = this.chart.xAxes.push(new am4charts.ValueAxis());
      // // Label
      valueAxis.renderer.labels.template.fill = am4core.color(this.fontColor);

      // // Grid
      valueAxis.min = -2;
      valueAxis.max = 2;
      valueAxis.renderer.minGridDistance = 150;

      valueAxis.renderer.baseGrid.stroke = am4core.color(this.fontColor);
      valueAxis.renderer.baseGrid.strokeOpacity = 1;
      valueAxis.renderer.baseGrid.strokeWidth = 1.5;

      valueAxis.renderer.grid.template.stroke = am4core.color(this.fontColor);
      valueAxis.renderer.grid.template.strokeOpacity = 0.5;
      valueAxis.renderer.grid.template.strokeDasharray = 5;
    },

    createSeries(cvc, number) {
      // Create series
      const series = this.chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueX = cvc;
      series.dataFields.categoryY = 'question';
      series.name = `CVC ${number}`;
      series.columns.template.fillOpacity = 1;
      const colourPalette = ['#bd95ff', '#99de82', '#f3c065', '#3aaace'];
      series.columns.template.fill = am4core.color(colourPalette[number - 1]);
      series.sequencedInterpolation = true;

      series.columns.template.tooltipText = '{categoryY}: [bold]{valueX}[/]';
      series.tooltip.background.strokeOpacity = 0;
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = am4core.color('white');
      series.tooltip.label.fill = am4core.color('black');

      const columnTemplate = series.columns.template;
      columnTemplate.strokeOpacity = 0;
      columnTemplate.height = 15;
      // columnTemplate.column.cornerRadiusBottomRight = 2;
      // columnTemplate.column.cornerRadiusTopRight = 2;

      const hoverState = series.columns.template.states.create('hover');
      hoverState.properties.fillOpacity = 0.5;
    },

    createLegend() {
      this.chart.legend = new am4charts.Legend();
      this.chart.legend.labels.template.fill = am4core.color(this.fontColor);
      this.chart.legend.position = 'top';
      this.chart.legend.contentAlign = 'right';
      this.chart.legend.itemContainers.template.stroke = am4core.color(this.fontColor);
      this.chart.legend.itemContainers.template.strokeOpacity = 0.8;
      this.chart.legend.itemContainers.template.strokeWidth = 1;
      this.chart.legend.itemContainers.template.paddingLeft = 5;
      this.chart.legend.itemContainers.template.paddingRight = 5;
      this.chart.legend.itemContainers.template.paddingBottom = 4;
      this.chart.legend.itemContainers.template.paddingTop = 6;
      this.chart.legend.itemContainers.template.marginBottom = 15;
      this.chart.legend.itemContainers.template.marginRight = 0;
      this.chart.legend.itemContainers.template.marginLeft = 20;

      this.chart.legend.labels.template.strokeWidth = 0;

      const markerTemplate = this.chart.legend.markers.template;
      markerTemplate.width = 20;
      markerTemplate.height = 13;

      // Custom disable style
      const as = this.chart.legend.itemContainers.template.states.getKey('active');
      as.properties.strokeOpacity = 0.2;
    },
  },
};
</script>

<style lang="scss" scoped>
  #cvcResult {
    display: flex;

    .leftLabel {
      width: 195px;
      display: flex;
      flex-wrap: wrap;
      flex-direction: column;
      justify-content: space-between;
      padding: 70px 0 90px;
      font-size: 12px;
      text-align: right;
      margin-right: -30px;
      position: relative;

      &.mobileMode {
        width: 30px;
      }

      .overall {
        font-size: 18px;
        position: absolute;
        top: 15px;
        left: 0;
        font-weight: bold;
        width: 300px;
        text-align: left;
        z-index: 500;

        .el-tooltip {
          font-size: 0.9rem;
        }
      }
    }

    .rightGraph {
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;

      .hello {
        width: 100%;
        height: 450px;
        font-size: 12px;
        font-style: normal;

        &.question2 {
          height: 500px;
        }

        &.dummyChart {
          filter: blur(2px) alpha(0.5);
        }
      }

      .bottomLabel {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        margin: -30px -15px 0 15px;

        div {
          display: flex;
          min-width: 5px;
          max-width: 65px;
          padding: 3px 10px;
          align-items: center;
        }

        &.mobileMode {
          padding-left: 30px;
          margin: -30px -10px 0 -10px;
        }
      }

      .noData {
        position: absolute;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        .placeholder {
          font-style: italic;
          max-width: calc(95% - 60px);
        }
      }
    }
  }
</style>
